<template>
  <!--
    HfSelect — the custom Tailwind select primitive (Vue 2.7, no Element UI).
    Covers: single-select with string / number / object values (`value-key` for
    object identity), clearable, local type-to-filter, an external filter hook
    (`filter-method`), remote/async (`remote` + `remote-method` + `loading`),
    a custom `#option` scoped slot, keyboard + click-away + a11y, `focus()`,
    clear-on-close, and size / placeholder / disabled.
    Preflight is OFF in this app, so every UA-styled element below carries a
    local reset (see TAILWIND_CONVENTIONS.md).
  -->
  <div
    v-click-away="close"
    class="hf-select relative w-full"
  >
    <div
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      class="flex items-center gap-2 w-full rounded-md border border-solid bg-white transition-colors"
      :class="[
        isOpen ? 'border-accent ring-1 ring-accent/20' : 'border-outline hover:border-muted-faint',
        disabled ? 'opacity-60 cursor-not-allowed bg-bg' : 'cursor-pointer',
        size === 'small' ? 'h-8 px-2.5' : 'h-10 px-3'
      ]"
      @click="onTriggerClick"
    >
      <input
        ref="input"
        :value="inputValue"
        :placeholder="inputPlaceholder"
        :readonly="!searchable"
        :disabled="disabled"
        type="text"
        autocomplete="off"
        class="flex-1 min-w-0 p-0 bg-transparent border-none outline-none truncate text-ink placeholder:text-muted-faint"
        :class="[
          size === 'small' ? 'text-sm' : 'text-sm',
          searchable ? '' : 'cursor-pointer',
          disabled ? 'cursor-not-allowed' : ''
        ]"
        @input="onInput"
        @keydown="onKeydown"
        @focus="onInputFocus"
      >

      <button
        v-if="clearable && hasValue && !disabled"
        type="button"
        tabindex="-1"
        class="shrink-0 flex items-center justify-center p-0 bg-transparent border-none cursor-pointer text-muted-faint hover:text-ink"
        @click.stop="clear"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" />
        </svg>
      </button>

      <button
        type="button"
        tabindex="-1"
        :disabled="disabled"
        class="shrink-0 flex items-center justify-center p-0 bg-transparent border-none cursor-pointer text-muted-faint transition-transform"
        :class="{ 'rotate-180': isOpen }"
        @click.stop="toggle"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3.5 5.5L7 9l3.5-3.5" />
        </svg>
      </button>
    </div>

    <div
      v-show="isOpen"
      class="hf-select__dropdown absolute z-50 mt-1 w-full rounded-md border border-solid border-line bg-white shadow-lg"
      :class="popperClass"
    >
      <ul
        class="hf-select__list m-0 p-1 list-none max-h-64 overflow-y-auto"
        role="listbox"
      >
        <li
          v-if="loading"
          class="px-3 py-2 text-sm text-center text-muted-faint"
        >
          {{ loadingLabel }}
        </li>
        <template v-else-if="displayOptions.length">
          <li
            v-for="(item, index) in displayOptions"
            :key="getKey(item, index)"
            ref="optionEls"
            :data-test-key="getTestKey(item)"
            role="option"
            :aria-selected="isSelected(item) ? 'true' : 'false'"
            class="flex items-center rounded px-3 cursor-pointer transition-colors"
            :class="[
              size === 'small' ? 'py-1.5 text-sm' : 'py-2 text-sm',
              index === highlightedIndex ? 'bg-row-hover' : '',
              isSelected(item) ? 'text-accent font-semibold' : 'text-ink'
            ]"
            @mousedown.prevent="select(item)"
            @mouseenter="highlightedIndex = index"
          >
            <slot name="option" :option="item">
              <span class="truncate">{{ getLabel(item) }}</span>
            </slot>
          </li>
        </template>
        <li
          v-else
          class="px-3 py-2 text-sm text-center text-muted-faint"
        >
          {{ query ? noMatchLabel : noDataLabel }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * @module HfSelect
 * The house select primitive. Element-free replacement for the Element select
 * used by BasicSelect / CountryPicker / NewsroomSearch and the feed language filter.
 */
export default {
  name: 'HfSelect',

  props: {
    /** v-model value: a primitive, or an object when `valueKey` is set. */
    value: {
      type: [String, Number, Object, Array, Boolean],
      default: null
    },
    /** The list of option items (objects or primitives). */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Object-identity key (Element `value-key` parity). When set, an option's
     * emitted value is the whole object and selection is compared by this key.
     */
    valueKey: {
      type: String,
      default: ''
    },
    /** Property to read an option's display label from (empty → the item itself). */
    labelKey: {
      type: String,
      default: ''
    },
    /** Property to read an option's emitted value from (ignored when `valueKey` set). */
    valueProp: {
      type: String,
      default: ''
    },
    /** Property used for the v-for `:key`. */
    keyProp: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    /** '' (default) | 'small'. */
    size: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    /** Show a search input and locally type-to-filter the options. */
    filterable: {
      type: Boolean,
      default: false
    },
    /** Remote/async mode — calls `remoteMethod(query)`; options are populated by the parent. */
    remote: {
      type: Boolean,
      default: false
    },
    remoteMethod: {
      type: Function,
      default: null
    },
    /**
     * External filter hook — called with the query string on every keystroke.
     * When provided, HfSelect does NOT filter locally (the parent narrows `options`).
     */
    filterMethod: {
      type: Function,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    /** Extra class(es) forwarded to the dropdown container (Element `popper-class` parity). */
    popperClass: {
      type: String,
      default: ''
    },
    loadingText: {
      type: String,
      default: null
    },
    noDataText: {
      type: String,
      default: null
    },
    noMatchText: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isOpen: false,
      query: '',
      highlightedIndex: -1,
      // Caches the label of the last picked option so the trigger keeps showing
      // it even when the option leaves `options` (remote reset / external filter).
      labelCache: null
    }
  },

  computed: {
    /** Whether a search input is shown (local filter, external filter or remote). */
    searchable () {
      return this.filterable || this.remote || !!this.filterMethod
    },
    /** Filtering is owned by the parent (remote or filter-method) — no local narrowing. */
    externallyFiltered () {
      return this.remote || !!this.filterMethod
    },
    displayOptions () {
      if (!this.filterable || this.externallyFiltered || !this.query) return this.options
      const q = this.query.toLowerCase()
      return this.options.filter(o => String(this.getLabel(o)).toLowerCase().includes(q))
    },
    hasValue () {
      return !this.isEmpty(this.value)
    },
    selectedLabel () {
      if (this.isEmpty(this.value)) return ''
      const match = this.options.find(o => this.isSelected(o))
      if (match) return this.getLabel(match)
      if (this.labelCache && this.valuesEqual(this.labelCache.value, this.value)) return this.labelCache.label
      return this.isPlain(this.value) ? String(this.value) : ''
    },
    inputValue () {
      return this.isOpen && this.searchable ? this.query : this.selectedLabel
    },
    inputPlaceholder () {
      return this.isOpen ? (this.selectedLabel || this.placeholder) : this.placeholder
    },
    loadingLabel () {
      return this.loadingText || this.$t('el.select.loading')
    },
    noDataLabel () {
      return this.noDataText || this.$t('el.select.noData')
    },
    noMatchLabel () {
      return this.noMatchText || this.$t('el.select.noMatch')
    }
  },

  watch: {
    options () {
      // Keep the highlight in range after the option set changes (remote/external filter).
      if (this.highlightedIndex >= this.displayOptions.length) {
        this.highlightedIndex = this.displayOptions.length ? 0 : -1
      }
    }
  },

  methods: {
    isEmpty (v) {
      return v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)
    },
    isPlain (v) {
      return typeof v === 'string' || typeof v === 'number'
    },
    getLabel (item) {
      if (this.labelKey) return item == null ? '' : item[this.labelKey]
      if (this.isPlain(item)) return item
      return (item && item.label != null) ? item.label : ''
    },
    getValue (item) {
      if (this.valueKey) return item
      if (this.valueProp) return item == null ? item : item[this.valueProp]
      return item
    },
    getKey (item, index) {
      if (this.valueKey && item != null) return item[this.valueKey]
      if (this.keyProp && item != null) return item[this.keyProp]
      const label = this.getLabel(item)
      return (label !== undefined && label !== '') ? label : index
    },
    getTestKey (item) {
      return this.valueKey ? this.getKey(item) : this.getValue(item)
    },
    valuesEqual (a, b) {
      if (this.valueKey) return !!a && !!b && a[this.valueKey] === b[this.valueKey]
      return a === b
    },
    isSelected (item) {
      if (this.isEmpty(this.value)) return false
      if (this.valueKey) return !!this.value && item != null && this.value[this.valueKey] === item[this.valueKey]
      return this.getValue(item) === this.value
    },
    open () {
      if (this.disabled || this.isOpen) return
      this.isOpen = true
      this.query = ''
      this.highlightedIndex = this.displayOptions.findIndex(o => this.isSelected(o))
      this.$emit('visible-change', true)
      this.$nextTick(() => {
        this.$refs.input && this.$refs.input.focus()
        this.scrollToHighlighted()
      })
    },
    close () {
      if (!this.isOpen) return
      this.isOpen = false
      this.query = ''
      this.highlightedIndex = -1
      this.$emit('visible-change', false)
    },
    toggle () {
      if (this.disabled) return
      this.isOpen ? this.close() : this.open()
    },
    onTriggerClick () {
      // The trigger only opens; closing is via the chevron, click-away, Esc or a pick.
      if (this.disabled) return
      this.open()
    },
    onInputFocus () {
      if (!this.disabled) this.open()
    },
    onInput (e) {
      if (!this.searchable) return
      this.query = e.target.value
      if (this.remote && this.remoteMethod) this.remoteMethod(this.query)
      else if (this.filterMethod) this.filterMethod(this.query)
      this.highlightedIndex = this.displayOptions.length ? 0 : -1
    },
    onKeydown (e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          this.isOpen ? this.moveHighlight(1) : this.open()
          break
        case 'ArrowUp':
          e.preventDefault()
          this.isOpen ? this.moveHighlight(-1) : this.open()
          break
        case 'Enter':
          e.preventDefault()
          if (this.isOpen && this.highlightedIndex > -1 && this.displayOptions[this.highlightedIndex]) {
            this.select(this.displayOptions[this.highlightedIndex])
          } else if (!this.isOpen) {
            this.open()
          }
          break
        case 'Escape':
          if (this.isOpen) {
            e.preventDefault()
            this.close()
          }
          break
        case 'Tab':
          this.close()
          break
      }
    },
    moveHighlight (dir) {
      const count = this.displayOptions.length
      if (!count) return
      let next = this.highlightedIndex
      next = (next + dir + count) % count
      this.highlightedIndex = next
      this.$nextTick(() => this.scrollToHighlighted())
    },
    scrollToHighlighted () {
      const els = this.$refs.optionEls
      const el = Array.isArray(els) ? els[this.highlightedIndex] : null
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    },
    select (item) {
      const val = this.getValue(item)
      this.labelCache = { value: val, label: this.getLabel(item) }
      this.$emit('input', val)
      this.$emit('change', val)
      this.close()
    },
    clear () {
      this.labelCache = null
      this.query = ''
      this.$emit('input', null)
      this.$emit('change', null)
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
    },
    /** Programmatic focus — opens the dropdown and focuses the input (parity with el-select.focus()). */
    focus () {
      this.open()
    }
  }
}
</script>
